import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import journeyService from '../services/journeyService';
import CreateJourneyModal from './CreateJourneyModal';
import JourneyCard from './JourneyCard';
import toast from 'react-hot-toast';

/**
 * Journeys Page
 * 🗺️ Main page for viewing and managing financial journeys
 */
const JourneysPage = () => {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchJourneys();
  }, []);

  const fetchJourneys = async () => {
    try {
      setLoading(true);
      const response = await journeyService.getAllJourneys();
      if (response.success) {
        setJourneys(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching journeys:', error);
      toast.error('Failed to load journeys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJourney = () => {
    setOpenModal(true);
  };

  const handleJourneyCreated = async (journeyData) => {
    try {
      // Create the journey
      const createResponse = await journeyService.createJourney(journeyData);
      
      if (createResponse.success) {
        const journeyId = createResponse.data._id;
        toast.success('Journey created successfully!');
        
        // Try to generate the plan, but don't fail if it doesn't work
        try {
          const generateResponse = await journeyService.generatePlan(journeyId);
          if (generateResponse.success) {
            toast.success('Journey plan generated!');
          } else {
            console.warn('Plan generation failed, but journey was created');
            toast('Journey created, but plan generation is still processing', { icon: 'ℹ️' });
          }
        } catch (generateError) {
          console.error('Error generating plan (non-fatal):', generateError);
          // Journey is still created, just plan generation failed
          toast('Journey created! Plan generation will be available shortly.', { icon: 'ℹ️' });
        }
        
        setOpenModal(false);
        fetchJourneys();
        // Navigate to the journey detail page
        navigate(`/journeys/${journeyId}`);
      } else {
        toast.error('Failed to create journey');
      }
    } catch (error) {
      console.error('Error creating journey:', error);
      toast.error('Failed to create journey');
    }
  };

  const handleDeleteJourney = async (id) => {
    if (!window.confirm('Are you sure you want to delete this journey?')) {
      return;
    }

    try {
      const response = await journeyService.deleteJourney(id);
      if (response.success) {
        toast.success('Journey deleted successfully');
        fetchJourneys();
      }
    } catch (error) {
      console.error('Error deleting journey:', error);
      toast.error('Failed to delete journey');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading journeys...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (journeys.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Plan Your Financial Journey
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Create personalized financial roadmaps to achieve your goals. Get step-by-step guidance 
              based on your unique situation, income, and aspirations.
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              What You'll Get
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🎯</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Multiple Goal Tracking</h3>
                  <p className="text-gray-600 text-sm">Track multiple financial goals simultaneously with personalized timelines.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📈</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Interactive Timelines</h3>
                  <p className="text-gray-600 text-sm">Visualize your progress with interactive charts showing savings and debt paydown.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">💰</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Tax-Optimized Strategies</h3>
                  <p className="text-gray-600 text-sm">Get recommendations based on best practices in accounting, finance, and tax law.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">💡</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Savings Opportunities</h3>
                  <p className="text-gray-600 text-sm">Identify where you can save money to accelerate your goal achievement.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <button
              onClick={handleCreateJourney}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 text-lg"
            >
              <span className="mr-2">🗺️</span>
              Create Your First Journey
            </button>
          </div>
        </div>

        {/* Create Journey Modal */}
        <CreateJourneyModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSuccess={handleJourneyCreated}
        />
      </div>
    );
  }

  // Journeys list view
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Journeys</h1>
            <p className="text-gray-600 mt-1">Manage your financial planning journeys</p>
          </div>
          <button
            onClick={handleCreateJourney}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200"
          >
            <span className="mr-2">+</span>
            Create Journey
          </button>
        </div>

        {/* Journeys Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {journeys.map((journey) => (
            <JourneyCard
              key={journey._id}
              journey={journey}
              onDelete={handleDeleteJourney}
              onView={() => navigate(`/journeys/${journey._id}`)}
            />
          ))}
        </div>

        {/* Create Journey Modal */}
        <CreateJourneyModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSuccess={handleJourneyCreated}
        />
      </div>
    </div>
  );
};

export default JourneysPage;

